"use client";

import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Link from "next/link";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";

import { ProductImage } from "@/components/catalog/product-image";
import { subtleTween } from "@/components/ui/motion-primitives";
import type {
  HomeCategoryBanner,
  HomeCategoryBannerProduct,
} from "@/domain/catalog/catalog-product";

const categoryIntervalMs = 10_000;
const productStepIntervalMs = 2_000;
const heroBannerGradientClasses = [
  "home-category-banner--band-horizontal",
  "home-category-banner--green-flow",
  "home-category-banner--navy-lilac-soft",
  "home-category-banner--aqua-violet",
  "home-category-banner--blue-gold-violet",
  "home-category-banner--blue-gold-violet",
] as const;

const MotionLink = motion.create(Link);

export interface HeroBannerProps {
  readonly banners: readonly HomeCategoryBanner[];
}

export function HeroBanner({ banners }: Readonly<HeroBannerProps>) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [productOffset, setProductOffset] = useState(0);
  const [maxProductOffset, setMaxProductOffset] = useState(0);
  const productsRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const activeBanner = banners[activeIndex] ?? banners[0];
  const displayProducts = activeBanner?.products ?? [];
  const categoryCount = banners.length;
  const setCategoryIndex = (nextIndex: number) => {
    const normalizedIndex = ((nextIndex % categoryCount) + categoryCount) % categoryCount;

    setActiveIndex(normalizedIndex);
    setProductOffset(0);
  };

  useEffect(() => {
    if (prefersReducedMotion || categoryCount < 2) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((currentIndex) => {
        setProductOffset(0);
        return (currentIndex + 1) % categoryCount;
      });
    }, categoryIntervalMs);

    return () => window.clearInterval(intervalId);
  }, [categoryCount, prefersReducedMotion]);

  useEffect(() => {
    const productsElement = productsRef.current;

    if (!productsElement || displayProducts.length === 0) {
      setMaxProductOffset(0);
      return;
    }

    const updateVisibleProductWindow = () => {
      const productElement = productsElement.querySelector<HTMLElement>(
        ".home-category-banner__product",
      );

      if (!productElement) {
        setMaxProductOffset(0);
        return;
      }

      const trackElement = productsElement.querySelector<HTMLElement>(
        ".home-category-banner__track",
      );
      const productWidth = productElement.getBoundingClientRect().width;
      const trackGap = trackElement
        ? Number.parseFloat(window.getComputedStyle(trackElement).columnGap)
        : 0;
      const stepWidth = productWidth + (Number.isNaN(trackGap) ? 0 : trackGap);
      const visibleProductCount = Math.max(
        1,
        Math.floor((productsElement.clientWidth + trackGap) / stepWidth),
      );

      setMaxProductOffset(Math.max(displayProducts.length - visibleProductCount, 0));
    };

    updateVisibleProductWindow();

    const resizeObserver = new ResizeObserver(updateVisibleProductWindow);
    resizeObserver.observe(productsElement);

    return () => resizeObserver.disconnect();
  }, [displayProducts.length, activeIndex]);

  useEffect(() => {
    if (prefersReducedMotion || maxProductOffset === 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setProductOffset((currentOffset) =>
        currentOffset >= maxProductOffset ? 0 : currentOffset + 1,
      );
    }, productStepIntervalMs);

    return () => window.clearInterval(intervalId);
  }, [maxProductOffset, prefersReducedMotion]);

  if (!activeBanner) {
    return null;
  }

  const categoryHref = `/categorias/${activeBanner.familySlug}`;
  const activeGradientClass =
    heroBannerGradientClasses[activeIndex % heroBannerGradientClasses.length] ??
    heroBannerGradientClasses[0];
  const productTrackStyle = {
    "--banner-product-offset": productOffset,
  } as CSSProperties;

  return (
    <section
      className={`home-category-banner ${activeGradientClass}`}
    >
      {banners.length > 1 ? (
        <>
          <button
            aria-label="Mostrar categoría anterior"
            className="home-category-banner__nav home-category-banner__nav--previous"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCategoryIndex(activeIndex - 1);
            }}
            type="button"
          >
            <ChevronLeft aria-hidden="true" size={22} strokeWidth={2} />
          </button>
          <button
            aria-label="Mostrar siguiente categoría"
            className="home-category-banner__nav home-category-banner__nav--next"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCategoryIndex(activeIndex + 1);
            }}
            type="button"
          >
            <ChevronRight aria-hidden="true" size={22} strokeWidth={2} />
          </button>
        </>
      ) : null}

      <AnimatePresence mode="wait">
        <MotionLink
          animate={{ opacity: 1 }}
          aria-label={`Ver categoría ${activeBanner.familyName}`}
          className="home-category-banner__link"
          exit={{ opacity: 0 }}
          href={categoryHref}
          initial={{ opacity: prefersReducedMotion ? 1 : 0 }}
          key={activeBanner.familySlug}
          prefetch={false}
          transition={subtleTween}
        >
          <div className="home-category-banner__copy">
            <h1 className="home-category-banner__title">{activeBanner.title}</h1>
            {activeBanner.subtitle ? (
              <p className="home-category-banner__subtitle">{activeBanner.subtitle}</p>
            ) : null}
            <span className="home-category-banner__cta">
              {activeBanner.ctaLabel}
              <ArrowRight aria-hidden="true" size={20} strokeWidth={1.8} />
            </span>
          </div>

          <div
            className="home-category-banner__products"
            key={activeBanner.familySlug}
            ref={productsRef}
          >
            <div
              className="home-category-banner__track"
              data-reduced-motion={prefersReducedMotion ? "true" : "false"}
              style={productTrackStyle}
            >
              {displayProducts.map((product, index) => (
                <span
                  className="home-category-banner__product"
                  key={product.id}
                >
                  <span className="home-category-banner__image-frame">
                    <ProductImage
                      alt=""
                      className="h-full w-full object-contain"
                      imageUrl={product.technicalSheetImageUrl ?? product.primaryImageUrl}
                      loading={index === 0 ? "eager" : "lazy"}
                      sizes="(max-width: 640px) 88px, 132px"
                    />
                  </span>
                  <ProductCaption product={product} />
                </span>
              ))}
            </div>
          </div>
        </MotionLink>
      </AnimatePresence>

      {banners.length > 1 ? (
        <div aria-label="Categorías destacadas" className="home-category-banner__dots">
          {banners.map((banner, index) => (
            <button
              aria-label={`Mostrar ${banner.familyName}`}
              aria-pressed={index === activeIndex}
              className="home-category-banner__dot"
              key={banner.familySlug}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCategoryIndex(index);
              }}
              type="button"
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ProductCaption({ product }: { readonly product: HomeCategoryBannerProduct }) {
  const caption = buildProductCaption(product);

  return (
    <span className="home-category-banner__caption">
      {caption.lines.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </span>
  );
}

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(query.matches);

    updatePreference();
    query.addEventListener("change", updatePreference);

    return () => query.removeEventListener("change", updatePreference);
  }, []);

  return prefersReducedMotion;
}

function buildProductCaption(product: HomeCategoryBannerProduct): {
  readonly lines: readonly string[];
} {
  const compatibleModels = product.compatibleModels
    .map((model) => model.trim())
    .filter(Boolean)
    .slice(0, 3);

  if (compatibleModels.length > 0) {
    return {
      lines: compatibleModels,
    };
  }

  const modelsFromName = extractModels(product.shortName ?? product.name);

  if (modelsFromName.length > 0) {
    return {
      lines: modelsFromName,
    };
  }

  const modelsFromSku = extractModels(product.sku.replace(/-/g, " "));

  if (modelsFromSku.length > 0) {
    return {
      lines: modelsFromSku,
    };
  }

  return {
    lines: [compactSku(product.sku)],
  };
}

function extractModels(value: string): string[] {
  const models = value.match(/\b[A-Z]?\d{3,5}[A-Z]?X?\b/gi);

  if (!models || models.length === 0) {
    return [];
  }

  return models
    .slice(0, 3)
    .map((model) => model.toUpperCase());
}

function compactSku(sku: string): string {
  return sku
    .split(/[-_\s]+/)
    .filter((part) => part.length >= 3)
    .slice(0, 2)
    .join(" ")
    .toUpperCase();
}
